import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Body, Button, Heading, I, Stack } from '@leanin/ui';
import { FlowShell } from '@/components/onboarding/FlowShell';
import { trackOnboardingStepCompleted } from '@/lib/analytics/events';
import { flowStepParams } from '@/lib/analytics/onboarding-steps';
import { LoopVideo } from '@/components/media/LoopVideo';
import welcomeMp4 from '@/assets/onboarding/welcome.mp4';
import welcomeWebm from '@/assets/onboarding/welcome.webm';
import welcomePoster from '@/assets/onboarding/welcome-poster.png?w=440;880;1320&format=avif;webp;png&as=picture';
import { NL_ROLE, NL_STEPS } from './-steps';

// Network Leader onboarding tour — step 1 (welcome letter from Nikki).
// Mirrors the CL welcome layout: hero gif at top + signed letter below.
export const Route = createFileRoute('/onboarding/nl/welcome')({
  component: NLOnboardingWelcomePage,
});

function NLOnboardingWelcomePage() {
  const navigate = useNavigate();
  return (
    <FlowShell
      role={NL_ROLE}
      stepIndex={0}
      steps={NL_STEPS}
      minutes={2}
      title="Welcome to the Lean In community — we are SO glad you’re here."
      primary={
        <Button
          variant="primary"
          iconRight={I.arrow}
          onClick={() => {
            trackOnboardingStepCompleted(
              flowStepParams('/onboarding/nl/welcome'),
            );
            navigate({ to: '/onboarding/nl/hub' });
          }}
        >
          Get started
        </Button>
      }
    >
      <Stack gap="lg">
        <LoopVideo
          webm={welcomeWebm}
          mp4={welcomeMp4}
          poster={welcomePoster}
          aria-label="Welcome to Lean In"
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />

        <Stack gap="md">
          <Heading level={2} style={{ fontStyle: 'italic' }}>
            Dear Network Leader,
          </Heading>
          <Body size="md">It is good to see you here.</Body>
          <Body size="md">
            This is not just another website. This is your space to connect,
            share, learn, and lead.
          </Body>
          <Body size="md">
            Here, you can be discovered and discover others. You can connect
            across borders, share resources, promote your events, and grow
            something meaningful, both as an individual and as a community
            builder.
          </Body>
          <Body size="md">
            Your profile reflects who you are. Your network page brings to
            life what you are doing in your community. Together, they are your
            signal to the world: there is space here, we are building
            something, and you are welcome.
          </Body>
          <Body size="md">
            As you set up your pages, be personal. Share why your network
            matters. Share what your members can expect. Don’t be perfect.
            Just be real. Leadership is, at its core, about creating spaces
            for others to thrive and that starts here.
          </Body>
          <Body size="md">
            We cannot wait to see the community you create and I’m here to
            help you along the way. Let’s get started.
          </Body>
          <Stack gap="xs">
            <Body size="md">— Nikki</Body>
            <Body size="sm" color="mute">
              Director, Global Network Leaders Program
            </Body>
          </Stack>
        </Stack>
      </Stack>
    </FlowShell>
  );
}
